import type {
  MovementControlRow,
  MovementControlStatus,
  MovementRawRow,
  MovementSortKey
} from "../types/inventoryMovement"

export type MovementKpi = {
  openingBalance: number
  totalIn: number
  totalOut: number
  closingBalance: number
  okCount: number
  warningCount: number
  errorCount: number
  controlScorePct: number
}

export function formatNumberId(value: number): string {
  return Number(value).toLocaleString("id-ID")
}

/** Ringkas angka besar seperti HTML `smartNumber` */
export function smartNumber(value: number): string {
  const number = Number(value) || 0
  const abs = Math.abs(number)
  if (abs >= 1_000_000_000)
    return `${(number / 1_000_000_000).toFixed(2).replace(/\.00$/, "")}B`
  if (abs >= 1_000_000)
    return `${(number / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`
  if (abs >= 1000)
    return `${(number / 1000).toFixed(1).replace(/\.0$/, "")}K`
  return number.toLocaleString("id-ID")
}

export function buildControlRows(rows: MovementRawRow[]): MovementControlRow[] {
  let previousClosing = 0
  let previousCost = rows[0]?.cost ?? 0

  return rows.map((row, index) => {
    const {
      date,
      transactionType,
      reference,
      location,
      stockIn,
      stockOut,
      actualClosing,
      cost,
      createdBy,
      note
    } = row

    const opening = index === 0 ? 0 : previousClosing
    const expectedClosing = opening + stockIn - stockOut
    const variance = actualClosing - expectedClosing
    const value = Math.max(actualClosing, 0) * cost

    let status: MovementControlStatus = "OK"
    let controlNote = "Validated"

    if (variance !== 0) {
      status = "ERROR"
      controlNote = `Balance variance detected: ${variance}`
    } else if (actualClosing < 0) {
      status = "ERROR"
      controlNote = "Negative stock detected"
    } else if (
      index > 0 &&
      previousCost !== 0 &&
      Math.abs(cost - previousCost) / previousCost > 0.02
    ) {
      status = "WARNING"
      controlNote = "Cost changed more than 2%"
    } else if (stockOut >= 50) {
      status = "WARNING"
      controlNote = "Unusually high stock out movement"
    }

    previousClosing = actualClosing
    previousCost = cost

    return {
      date,
      transactionType,
      reference,
      location,
      opening,
      stockIn,
      stockOut,
      expectedClosing,
      actualClosing,
      variance,
      cost,
      value,
      status,
      controlNote,
      createdBy,
      note
    }
  })
}

export function computeMovementKpi(data: MovementControlRow[]): MovementKpi {
  if (!data.length) {
    return {
      openingBalance: 0,
      totalIn: 0,
      totalOut: 0,
      closingBalance: 0,
      okCount: 0,
      warningCount: 0,
      errorCount: 0,
      controlScorePct: 0
    }
  }

  const openingBalance = data[0].actualClosing
  const totalIn = data.reduce((s, r) => s + r.stockIn, 0)
  const totalOut = data.reduce((s, r) => s + r.stockOut, 0)
  const closingBalance = data[data.length - 1].actualClosing
  const okCount = data.filter((r) => r.status === "OK").length
  const warningCount = data.filter((r) => r.status === "WARNING").length
  const errorCount = data.filter((r) => r.status === "ERROR").length
  const controlScorePct = Math.round((okCount / data.length) * 100)

  return {
    openingBalance,
    totalIn,
    totalOut,
    closingBalance,
    okCount,
    warningCount,
    errorCount,
    controlScorePct
  }
}

export function auditExplanation(status: MovementControlStatus): string {
  if (status === "OK")
    return "Transaction is valid. Movement matches calculation logic."
  if (status === "WARNING")
    return "Movement is unusual. Requires review but still acceptable."
  return "Critical issue detected. Reconciliation required immediately."
}

export function getSortValue(
  row: MovementControlRow,
  key: MovementSortKey
): string | number {
  switch (key) {
    case "date":
      return new Date(row.date).getTime()
    case "transactionType":
      return row.transactionType.toLowerCase()
    case "reference":
      return row.reference.toLowerCase()
    case "location":
      return row.location.toLowerCase()
    case "opening":
      return row.opening
    case "stockIn":
      return row.stockIn
    case "stockOut":
      return row.stockOut
    case "expectedClosing":
      return row.expectedClosing
    case "actualClosing":
      return row.actualClosing
    case "variance":
      return row.variance
    case "cost":
      return row.cost
    case "value":
      return row.value
    case "status":
      return row.status
    default:
      return ""
  }
}

export function sortMovementRows(
  rows: MovementControlRow[],
  key: MovementSortKey,
  asc: boolean
): MovementControlRow[] {
  const next = [...rows]
  next.sort((a, b) => {
    const va = getSortValue(a, key)
    const vb = getSortValue(b, key)
    if (typeof va === "string" && typeof vb === "string") {
      const c = va.localeCompare(vb)
      return asc ? c : -c
    }
    const na = Number(va)
    const nb = Number(vb)
    if (na < nb) return asc ? -1 : 1
    if (na > nb) return asc ? 1 : -1
    return 0
  })
  return next
}

export function filterMovementRows(
  all: MovementControlRow[],
  params: {
    dateFrom: string
    dateTo: string
    location: string
    controlStatus: "All" | MovementControlStatus
    transactionType: string
    search: string
  }
): MovementControlRow[] {
  const q = params.search.trim().toLowerCase()
  return all.filter((row) => {
    const searchable =
      `${row.transactionType} ${row.reference} ${row.location} ${row.controlNote} ${row.createdBy} ${row.note}`.toLowerCase()

    const dateMatch =
      (!params.dateFrom || row.date >= params.dateFrom) &&
      (!params.dateTo || row.date <= params.dateTo)
    const locationMatch =
      params.location === "All Locations" || row.location === params.location
    const typeMatch =
      params.transactionType === "All" ||
      row.transactionType === params.transactionType
    const statusMatch =
      params.controlStatus === "All" || row.status === params.controlStatus
    const searchMatch = !q || searchable.includes(q)

    return (
      dateMatch &&
      locationMatch &&
      typeMatch &&
      statusMatch &&
      searchMatch
    )
  })
}
